import { useEffect, useState, useMemo } from "react";
import { api, fmtINR } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { TrendingUp, Download, FileText, Settings, Upload, AlertTriangle, ArrowUpRight, ArrowDownRight } from "lucide-react";
import {
  ComposedChart, Line, Bar, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";

const mtr = (n) => Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 }) + " m";

const VEL = {
  high: { label: "High", cls: "bg-[#ECFDF5] text-[#065F46] border-[#A7F3D0]" },
  low: { label: "Low / Deadstock", cls: "bg-[#FEF2F2] text-[#991B1B] border-[#FECACA]" },
  steady: { label: "Steady", cls: "bg-[#EFF6FF] text-[#1E40AF] border-[#BFDBFE]" },
};
const RISK = { High: "text-[#991B1B]", Medium: "text-[#92400E]", Low: "text-[#065F46]" };

export default function FabricDemand() {
  const { user } = useAuth();
  const canView = user?.role === "admin" || !!user?.analytics_access;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [horizon, setHorizon] = useState("30");
  const [active, setActive] = useState({});
  const [busy, setBusy] = useState("");
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settings, setSettings] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/analytics/forecast")
      .then((r) => {
        setData(r.data);
        const on = {};
        (r.data.categories || []).forEach((c) => { on[c.name] = true; });
        setActive(on);
      })
      .catch(() => toast.error("Could not load forecast."))
      .finally(() => setLoading(false));
  };
  useEffect(() => { if (canView) load(); else setLoading(false); }, [canView]);

  const cats = useMemo(() => (data?.categories || []).filter((c) => active[c.name]), [data, active]);

  const horizonMeters = (c) => horizon === "30" ? c.next_month_meters : horizon === "quarter" ? c.quarter_meters : c.year_meters;
  const horizonRevenue = (c) => horizon === "30" ? c.next_month_revenue : horizon === "quarter" ? c.quarter_revenue : c.year_revenue;
  const hLabel = horizon === "30" ? "Next 30 Days" : horizon === "quarter" ? "Next Quarter" : "Next 12 Months";

  const kpi = useMemo(() => {
    const cap = data?.kpis?.monthly_capacity_meters || 0;
    const projMeters = cats.reduce((s, c) => s + horizonMeters(c), 0);
    const projRev = cats.reduce((s, c) => s + horizonRevenue(c), 0);
    const nextSum = cats.reduce((s, c) => s + c.next_month_meters, 0);
    const capUtil = cap ? Math.round((nextSum / cap) * 100) : 0;
    const slow = cats.filter((c) => c.velocity === "low").reduce((s, c) => s + c.capital_lockup, 0);
    return { projMeters, projRev, capUtil, slow };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cats, horizon, data]);

  const blob = (res, name) => {
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url; a.download = name; a.click();
    URL.revokeObjectURL(url);
  };

  const downloadPdf = async () => {
    setBusy("pdf");
    try {
      const res = await api.post("/analytics/generate-pdf", {}, { responseType: "blob" });
      blob(res, "fabric-demand-forecast.pdf");
    } catch { toast.error("Could not generate PDF."); }
    finally { setBusy(""); }
  };
  const exportCsv = async () => {
    setBusy("csv");
    try {
      const res = await api.get("/analytics/export-csv", { responseType: "blob" });
      blob(res, "fabric-demand-dataset.csv");
    } catch { toast.error("Could not export CSV."); }
    finally { setBusy(""); }
  };
  const uploadHist = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy("upload");
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data: r } = await api.post("/analytics/upload-historical", fd);
      toast.success(`Imported ${r.rows} historical rows.`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed."); }
    finally { setBusy(""); e.target.value = ""; }
  };

  const openSettings = async () => {
    try {
      const { data: s } = await api.get("/analytics/settings");
      setSettings(s);
      setSettingsOpen(true);
    } catch { toast.error("Could not load assumptions."); }
  };
  const saveSettings = async () => {
    setBusy("settings");
    try {
      await api.put("/analytics/settings", settings);
      toast.success("Assumptions saved.");
      setSettingsOpen(false);
      load();
    } catch { toast.error("Could not save."); }
    finally { setBusy(""); }
  };

  if (!canView) return <div className="text-sm text-[#991B1B] font-medium" data-testid="analytics-forbidden">Fabric Demand Intelligence is not enabled for your account. Ask an administrator to grant access.</div>;
  if (loading) return <div className="text-slate-400 font-mono text-sm">Loading forecast…</div>;
  if (!data) return <div className="text-slate-400 font-mono text-sm">No forecast available.</div>;

  if (!data.has_data) {
    return (
      <div className="space-y-4" data-testid="fabric-demand-page">
        <h1 className="font-heading text-4xl font-bold text-[#0A0D14]">Fabric Demand Intelligence</h1>
        <div className="bg-white border border-[#E6E4DD] rounded-sm p-10 text-center text-sm text-[#4B5563]">{data.note}</div>
      </div>
    );
  }

  const trajectory = (data.total_series || []).map((p) => ({
    label: p.label, actual: p.actual, forecast: p.forecast, lower95: p.lower95, upper95: p.upper95,
  }));
  const pareto = (data.pareto || []).map((p) => ({ name: p.name.split(" ")[0], full: p.name, meters: p.meters, cum: p.cum_pct }));

  return (
    <div className="space-y-6" data-testid="fabric-demand-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-[#8CA3A7] flex items-center gap-1.5"><TrendingUp className="w-3.5 h-3.5" /> Analytics</p>
          <h1 className="font-heading text-4xl font-bold text-[#0A0D14] tracking-tight mt-1">Fabric Demand Intelligence</h1>
          <p className="text-sm text-[#4B5563] mt-1">Forecast period: <span className="font-medium">{data.period_label}</span> · {data.kpis.category_count} categories</p>
        </div>
        <div className="flex items-center gap-2">
          <label data-testid="upload-historical-btn" className="cursor-pointer bg-white text-[#1C2331] border border-[#E6E4DD] rounded-sm px-3 py-2.5 text-sm font-medium flex items-center gap-2 hover:border-[#1C2331] transition-colors">
            <Upload className="w-4 h-4" /> {busy === "upload" ? "Uploading…" : "Import History"}
            <input type="file" accept=".csv,.json" className="hidden" onChange={uploadHist} />
          </label>
          <button data-testid="analytics-settings-btn" onClick={openSettings} className="bg-white text-[#1C2331] border border-[#E6E4DD] rounded-sm px-3 py-2.5 text-sm font-medium flex items-center gap-2 hover:border-[#1C2331] transition-colors">
            <Settings className="w-4 h-4" /> Assumptions
          </button>
          <button data-testid="export-csv-btn" onClick={exportCsv} disabled={busy === "csv"} className="bg-white text-[#1C2331] border border-[#E6E4DD] rounded-sm px-3 py-2.5 text-sm font-medium flex items-center gap-2 hover:border-[#1C2331] transition-colors disabled:opacity-60">
            <FileText className="w-4 h-4" /> {busy === "csv" ? "Exporting…" : "Export CSV"}
          </button>
          <button data-testid="download-executive-pdf-btn" onClick={downloadPdf} disabled={busy === "pdf"} className="bg-[#D95D39] text-white rounded-sm px-4 py-2.5 text-sm font-medium flex items-center gap-2 hover:bg-[#c24e2e] transition-colors disabled:opacity-60">
            <Download className="w-4 h-4" /> {busy === "pdf" ? "Rendering…" : "Download Executive PDF"}
          </button>
        </div>
      </div>

      {data.history_months_count < 12 && (
        <div data-testid="limited-history-note" className="flex items-start gap-2 rounded-sm border border-[#FDE68A] bg-[#FFFBEB] px-4 py-3 text-xs text-[#92400E]">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>Only {data.history_months_count} month(s) of history available — figures are trend-based projections, not seasonal forecasts. Import more monthly dispatch history to sharpen accuracy.</span>
        </div>
      )}

      {/* Horizon toggle */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs uppercase tracking-wider text-[#8CA3A7] mr-1">Horizon</span>
        {[["30", "Next 30 Days"], ["quarter", "Next Quarter"], ["year", "Next 12 Months"]].map(([v, l]) => (
          <button key={v} data-testid={`horizon-${v}`} onClick={() => setHorizon(v)}
            className={`text-sm px-3 py-1.5 rounded-sm border transition-colors ${horizon === v ? "bg-[#1C2331] text-white border-[#1C2331]" : "bg-white text-[#4B5563] border-[#E6E4DD] hover:border-[#1C2331]"}`}>{l}</button>
        ))}
      </div>

      {/* KPI grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="analytics-kpis">
        {[
          { l: `Projected Demand (${hLabel})`, v: mtr(kpi.projMeters), t: "kpi-proj-meters" },
          { l: `Projected Gross Revenue (${hLabel})`, v: fmtINR(kpi.projRev), t: "kpi-proj-revenue" },
          { l: "Capacity Utilization (next month)", v: `${kpi.capUtil}%`, t: "kpi-capacity" },
          { l: "Capital Lockup — Slow Lines", v: fmtINR(kpi.slow), t: "kpi-capital-lockup" },
        ].map((c) => (
          <div key={c.t} data-testid={c.t} className="bg-white border border-[#E6E4DD] rounded-sm p-4">
            <p className="text-[11px] uppercase tracking-wider text-[#8CA3A7] leading-tight">{c.l}</p>
            <p className="font-heading text-2xl font-bold text-[#0A0D14] tabular-nums mt-2">{c.v}</p>
          </div>
        ))}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white border border-[#E6E4DD] rounded-sm p-5">
          <h2 className="font-heading text-lg font-semibold text-[#0A0D14] mb-1">12-Month Demand Trajectory</h2>
          <p className="text-xs text-[#8CA3A7] mb-3">Total meters — actual (dark) vs trend projection (orange) with 95% band.</p>
          <ResponsiveContainer width="100%" height={260}>
            <ComposedChart data={trajectory} margin={{ top: 5, right: 10, bottom: 5, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EEF0EC" />
              <XAxis dataKey="label" tick={{ fontSize: 10 }} angle={-30} textAnchor="end" height={50} />
              <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => (v >= 100000 ? `${(v / 100000).toFixed(0)}L` : v)} />
              <Tooltip formatter={(v) => (v == null ? "—" : mtr(v))} />
              <Area type="monotone" dataKey="upper95" stroke="none" fill="#D95D39" fillOpacity={0.08} />
              <Area type="monotone" dataKey="lower95" stroke="none" fill="#F6F5F2" fillOpacity={1} />
              <Line type="monotone" dataKey="actual" stroke="#1C2331" strokeWidth={2.5} dot={false} name="Actual" connectNulls />
              <Line type="monotone" dataKey="forecast" stroke="#D95D39" strokeWidth={2.5} strokeDasharray="5 4" dot={false} name="Projection" connectNulls />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white border border-[#E6E4DD] rounded-sm p-5">
          <h2 className="font-heading text-lg font-semibold text-[#0A0D14] mb-1">Category Pareto (80/20)</h2>
          <p className="text-xs text-[#8CA3A7] mb-3">Historical meters per category with cumulative share.</p>
          <ResponsiveContainer width="100%" height={260}>
            <ComposedChart data={pareto} margin={{ top: 5, right: 10, bottom: 5, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EEF0EC" />
              <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-20} textAnchor="end" height={50} />
              <YAxis yAxisId="l" tick={{ fontSize: 10 }} tickFormatter={(v) => (v >= 100000 ? `${(v / 100000).toFixed(0)}L` : v)} />
              <YAxis yAxisId="r" orientation="right" domain={[0, 100]} tick={{ fontSize: 10 }} tickFormatter={(v) => `${v}%`} />
              <Tooltip formatter={(v, n) => (n === "Cumulative %" ? `${v}%` : mtr(v))} labelFormatter={(l, p) => p?.[0]?.payload?.full || l} />
              <Bar yAxisId="l" dataKey="meters" fill="#1C2331" name="Meters" radius={[2, 2, 0, 0]} />
              <Line yAxisId="r" type="monotone" dataKey="cum" stroke="#D95D39" strokeWidth={2} dot={{ r: 2 }} name="Cumulative %" />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Category filter chips */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs uppercase tracking-wider text-[#8CA3A7] mr-1">Categories</span>
        {(data.categories || []).map((c) => (
          <button key={c.name} data-testid={`cat-filter-${c.name}`} onClick={() => setActive((p) => ({ ...p, [c.name]: !p[c.name] }))}
            className={`text-xs px-2.5 py-1 rounded-sm border transition-colors ${active[c.name] ? "bg-[#1C2331] text-white border-[#1C2331]" : "bg-white text-[#8CA3A7] border-[#E6E4DD] line-through"}`}>{c.name}</button>
        ))}
      </div>

      {/* Analyst commentary */}
      <div className="bg-[#1C2331] text-white rounded-sm p-6" data-testid="analyst-commentary">
        <h2 className="font-heading text-lg font-semibold mb-2 flex items-center gap-2"><TrendingUp className="w-4 h-4 text-[#D95D39]" /> Executive Analyst Insights</h2>
        <p className="text-sm text-slate-200 leading-relaxed">{data.commentary.summary}</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-4">
          <div>
            <p className="text-xs uppercase tracking-wider text-[#A7F3D0] mb-2 flex items-center gap-1"><ArrowUpRight className="w-3.5 h-3.5" /> Surge & Procurement</p>
            <ul className="space-y-1.5">
              {(data.commentary.surge.length ? data.commentary.surge : ["No surge categories this cycle."]).map((s, i) => (
                <li key={i} className="text-xs text-slate-300 leading-snug">• {s}</li>
              ))}
            </ul>
          </div>
          <div>
            <p className="text-xs uppercase tracking-wider text-[#FECACA] mb-2 flex items-center gap-1"><ArrowDownRight className="w-3.5 h-3.5" /> Deadstock & Liquidation</p>
            <ul className="space-y-1.5">
              {(data.commentary.deadstock.length ? data.commentary.deadstock : ["No deadstock risk this cycle."]).map((s, i) => (
                <li key={i} className="text-xs text-slate-300 leading-snug">• {s}</li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Category table */}
      <div className="border border-[#E6E4DD] rounded-sm bg-white overflow-x-auto" data-testid="category-table">
        <table className="w-full text-sm text-left min-w-[900px]">
          <thead>
            <tr className="font-mono text-[10px] uppercase tracking-wider text-[#8CA3A7] bg-slate-50 border-b border-[#E6E4DD]">
              <th className="px-4 py-3">Category</th>
              <th className="px-4 py-3">Weave</th>
              <th className="px-4 py-3 text-right">Share</th>
              <th className="px-4 py-3">Velocity</th>
              <th className="px-4 py-3 text-right">Proj. {hLabel}</th>
              <th className="px-4 py-3 text-right">Proj. Revenue</th>
              <th className="px-4 py-3 text-right">Growth</th>
              <th className="px-4 py-3 text-right">Stock</th>
              <th className="px-4 py-3 text-right">Sugg. Batch</th>
              <th className="px-4 py-3 text-center">Stock-out</th>
              <th className="px-4 py-3 text-center">MAPE</th>
            </tr>
          </thead>
          <tbody>
            {cats.map((c) => (
              <tr key={c.name} data-testid={`cat-row-${c.name}`} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/60">
                <td className="px-4 py-3">
                  <div className="text-[#0A0D14] font-medium">{c.name}</div>
                  {c.examples?.[0] && <div className="text-[11px] text-[#8CA3A7] truncate max-w-[220px]">{c.examples[0]}</div>}
                </td>
                <td className="px-4 py-3 text-[#4B5563] text-xs">{c.weave}</td>
                <td className="px-4 py-3 text-right font-mono tabular-nums text-[#4B5563]">{c.share_pct}%</td>
                <td className="px-4 py-3"><span className={`text-[10px] uppercase tracking-wider font-mono px-2 py-0.5 rounded-sm border ${VEL[c.velocity].cls}`}>{VEL[c.velocity].label}</span></td>
                <td className="px-4 py-3 text-right font-mono tabular-nums text-[#0A0D14]">{mtr(horizonMeters(c))}</td>
                <td className="px-4 py-3 text-right font-mono tabular-nums text-[#4B5563]">{fmtINR(horizonRevenue(c))}</td>
                <td className={`px-4 py-3 text-right font-mono tabular-nums ${c.growth_pct >= 0 ? "text-[#065F46]" : "text-[#991B1B]"}`}>{c.growth_pct >= 0 ? "+" : ""}{c.growth_pct}%</td>
                <td className="px-4 py-3 text-right font-mono tabular-nums text-[#4B5563]">{mtr(c.stock_meters)}</td>
                <td className="px-4 py-3 text-right font-mono tabular-nums text-[#0A0D14]">{mtr(c.suggested_batch)}</td>
                <td className={`px-4 py-3 text-center text-xs font-semibold ${RISK[c.stockout_risk]}`}>{c.stockout_risk}</td>
                <td className="px-4 py-3 text-center font-mono tabular-nums text-[#8CA3A7] text-xs">{c.mape != null ? `${c.mape}%` : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Settings dialog */}
      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent className="rounded-sm max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="analytics-settings-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Forecast assumptions</DialogTitle>
            <DialogDescription className="text-xs text-[#8CA3A7]">Since stock, lead-time and holding cost aren't in the order data, set them here per category. Used for stock-out risk, suggested batches and deadstock capital lock-up.</DialogDescription>
          </DialogHeader>
          {settings && (
            <div className="space-y-4">
              <div>
                <label className="text-xs uppercase tracking-wider text-[#8CA3A7]">Monthly weaving capacity (meters)</label>
                <input data-testid="setting-capacity" type="number" value={settings.monthly_capacity_meters}
                  onChange={(e) => setSettings({ ...settings, monthly_capacity_meters: Number(e.target.value) })}
                  className="mt-1 w-full rounded-sm border border-[#E6E4DD] px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#1C2331]" />
              </div>
              <div className="border border-[#E6E4DD] rounded-sm overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="font-mono text-[10px] uppercase tracking-wider text-[#8CA3A7] bg-slate-50 border-b border-[#E6E4DD]">
                      <th className="px-3 py-2 text-left">Category</th>
                      <th className="px-3 py-2 text-right">Stock (m)</th>
                      <th className="px-3 py-2 text-right">Lead (days)</th>
                      <th className="px-3 py-2 text-right">Holding ₹/m/mo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(settings.categories || {}).map(([cat, v]) => (
                      <tr key={cat} className="border-b border-slate-100 last:border-0">
                        <td className="px-3 py-2 text-[#0A0D14]">{cat}</td>
                        {["stock_meters", "lead_time_days", "holding_cost"].map((f) => (
                          <td key={f} className="px-3 py-2 text-right">
                            <input data-testid={`setting-${cat}-${f}`} type="number" value={v[f]}
                              onChange={(e) => setSettings({ ...settings, categories: { ...settings.categories, [cat]: { ...v, [f]: Number(e.target.value) } } })}
                              className="w-24 rounded-sm border border-[#E6E4DD] px-2 py-1 text-sm text-right focus:outline-none focus:ring-1 focus:ring-[#1C2331]" />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <button data-testid="save-settings-btn" onClick={saveSettings} disabled={busy === "settings"}
                className="w-full bg-[#1C2331] text-white rounded-sm px-4 py-2.5 text-sm font-medium hover:bg-[#2A3447] transition-colors disabled:opacity-60">
                {busy === "settings" ? "Saving…" : "Save assumptions"}
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
